import { useCallback, useEffect, useRef, useState } from "react";
import { AudioChat, VOICE_CHAT_EVENTS } from "../AudioChat";
import { ChatMessage, VoiceBotOptions } from "../types";
import { getSimulatorEndpoint } from "../api";
import { useAppDispatch } from "../../../redux/hooks";
import { addMessage, emptyChat, putChat, setSelectedChatID } from "../../../redux/voice-bot/chat-history.slice";

interface UseVoiceChatSessionProps {
    versionEndpoint: string;
    options: VoiceBotOptions;
}

export const useVoiceChatSession = ({ versionEndpoint, options }: UseVoiceChatSessionProps) => {
    const dispatch = useAppDispatch();
    const [isStarted, setIsStarted] = useState(false);

    const handleAddChat = useCallback(() => {
        const newChat = emptyChat();
        dispatch(putChat(newChat));
        dispatch(setSelectedChatID(newChat.id));
    }, [dispatch]);

    const initNewBot = useCallback(() => {
        const vb = new AudioChat(getSimulatorEndpoint(versionEndpoint), options);

        vb.on(VOICE_CHAT_EVENTS.MESSAGE, (message: ChatMessage) => {
            dispatch(addMessage(message));
        });

        vb.on(VOICE_CHAT_EVENTS.STARTED, () => {
            handleAddChat();
            setIsStarted(true);
        });

        vb.on(VOICE_CHAT_EVENTS.STOPPED, () => {
            voiceChat.current = initNewBot();
            setIsStarted(false);
        });

        return vb;
    }, [versionEndpoint, options, dispatch, handleAddChat]);

    const voiceChat = useRef(initNewBot());

    useEffect(() => {
        if (voiceChat.current) {
            voiceChat.current.setOptions(options);
        }
    }, [options]);

    useEffect(() => {
        if (voiceChat.current) {
            voiceChat.current.setEndpoint(getSimulatorEndpoint(versionEndpoint));
        }
    }, [versionEndpoint]);

    const start = useCallback(() => {
        voiceChat.current.start();
    }, []);

    const stop = useCallback(() => {
        voiceChat.current.stop();
    }, []);

    return {
        voiceChat: voiceChat.current,
        isStarted,
        start,
        stop,
        handleAddChat,
    };
};
